import {
  APP_BASE_HREF,
  CommonModule,
  PlatformLocation,
  registerLocaleData,
} from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEnGB from '@angular/common/locales/en-GB';
import localeFrCH from '@angular/common/locales/fr-CH';
import localeItCH from '@angular/common/locales/it-CH';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FORMLY_CONFIG, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Configuration,
  ConfigurationParameters,
  XatApiModule,
} from 'net-api-lib';
import { environment } from '../../environments/environment';
import {
  Configuration as LegacyConfiguration,
  ConfigurationParameters as LegacyConfigurationParameters,
  XatenaApiModule as LegacyXatenaApiModule,
} from '../xatena-api';
import { AuthInterceptor } from './auth/auth.interceptor';
import { AuthService } from './auth/auth.service';
import { registerFormlyTranslateExtension } from './formly.translate.extension';

import { MAT_DATE_LOCALE } from '@angular/material/core';
import { CookieService } from 'ngx-cookie-service';
import { ISlimScrollOptions, SLIMSCROLL_DEFAULTS } from 'ngx-slimscroll';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SimapAuthInterceptor } from './auth/simap-auth.interceptor';
import { LanguageService } from './language.service';
import { UserInfoService } from './user-info.service';

// register locales for supported languages
// overwrite shortDate , angular default is "dd.MM.yy" for xxCH -> nobody uses 2-digit years anymore
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(localeDeCH as any)[10][0] = 'dd.MM.yyyy';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(localeFrCH as any)[10][0] = 'dd.MM.yyyy';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(localeItCH as any)[10][0] = 'dd.MM.yyyy';

registerLocaleData(localeDeCH, 'de');
registerLocaleData(localeFrCH, 'fr');
registerLocaleData(localeItCH, 'it');
registerLocaleData(localeEnGB, 'en');

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  exports: [
    ReactiveFormsModule,
    TranslateModule,
    FormlyModule,
    LegacyXatenaApiModule,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormlyModule.forRoot({
      extras: {
        lazyRender: true,
        checkExpressionOn: 'changeDetectionCheck',
      },
      types: [],
    }),
    FormlyMaterialModule,
    TranslateModule.forRoot(),
    LegacyXatenaApiModule.forRoot({
      provide: LegacyConfiguration,
      useFactory: legacyApiConfigFactory,
    }),
    XatApiModule.forRoot(apiConfigFactory),
  ],
  providers: [
    [CookieService],
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [AuthService, UserInfoService],
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: registerFormlyTranslateExtension,
      deps: [TranslateService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: legacyAuthorizeInterceptorFactory,
      deps: [AuthService, Router, LegacyConfiguration],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: authorizeInterceptorFactory,
      deps: [AuthService, Router, Configuration],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: simapAuthorizeInterceptorFactory,
      deps: [Configuration],
      multi: true,
    },
    {
      provide: SLIMSCROLL_DEFAULTS,
      useValue: {
        alwaysVisible: true,
        gridOpacity: '0.2',
        barOpacity: '0.5',
        gridBackground: '#c2c2c2',
        gridWidth: '6',
        gridMargin: '2px 2px',
        barBackground: '#2C3E50',
        barWidth: '6',
        barMargin: '2px 2px',
      } as ISlimScrollOptions,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'de-CH',
    },
  ],
})
export class CoreModule {}

export function matDateFactory(
  languageService: LanguageService
): () => Observable<string> {
  return () => languageService.currentLanguage.pipe(map(m => m.culture));
}

export function apiConfigFactory(): Configuration {
  if (
    !environment.netApiUrl.startsWith('http://') &&
    !environment.netApiUrl.startsWith('https://')
  ) {
    if (!environment.netApiUrl.startsWith('/')) {
      environment.netApiUrl = `/${environment.netApiUrl}`;
    }

    environment.netApiUrl = `${location.protocol}//${location.host}${environment.netApiUrl}`;
  }

  const params: ConfigurationParameters = {
    basePath: environment.netApiUrl,
    withCredentials: false,
  };

  return new Configuration(params);
}

export function legacyApiConfigFactory(): LegacyConfiguration {
  if (
    !environment.API_BASE_PATH.startsWith('http://') &&
    !environment.API_BASE_PATH.startsWith('https://')
  ) {
    if (!environment.API_BASE_PATH.startsWith('/')) {
      environment.API_BASE_PATH = `/${environment.API_BASE_PATH}`;
    }

    environment.API_BASE_PATH = `${location.protocol}//${location.host}${environment.API_BASE_PATH}`;
  }

  const params: LegacyConfigurationParameters = {
    basePath: environment.API_BASE_PATH, //config.get('API_BASE_PATH'),
    withCredentials: false, //TODO: switch to true as soon as CORS orgin headers are correclty set (not *)
  };
  return new LegacyConfiguration(params);
}

export function legacyAuthorizeInterceptorFactory(
  authorize: AuthService,
  router: Router,
  apiConfiguration: LegacyConfiguration
): AuthInterceptor {
  return new AuthInterceptor(
    authorize,
    apiConfiguration.basePath ?? '',
    router
  );
}

export function authorizeInterceptorFactory(
  authorize: AuthService,
  router: Router,
  apiConfiguration: Configuration
): AuthInterceptor {
  return new AuthInterceptor(
    authorize,
    apiConfiguration.basePath ?? '',
    router
  );
}

export function simapAuthorizeInterceptorFactory(
  apiConfiguration: Configuration
): SimapAuthInterceptor {
  return new SimapAuthInterceptor(apiConfiguration.basePath ?? '');
}

/**
 * ensures sign in from local storage runs before anything else
 * @param authService
 * @param userInfoService
 * @returns
 */
function initializeAppFactory(
  authService: AuthService
): () => Observable<boolean> {
  return () => authService.trySignInFromLocalStorage();
}
